import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ChatHeader from './components/ChatHeader';
import WelcomeMessage from './components/WelcomeMessage';
import ConversationArea from './components/ConversationArea';
import ChatInput from './components/ChatInput';
import QuickActions from './components/QuickActions';

const AIAssistantInterface = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);

  // AI Assistant Knowledge Base
  const aiResponses = {
    pricing: `Our pricing is designed to be transparent, honest, and affordable for growing businesses:\n\n• 🌐 **Static Website**: ₹15,000 - ₹50,000\n• ⚙️ **Dynamic Web Application**: ₹50,000 - ₹3,00,000\n• 🛒 **E-commerce Platform**: ₹1,50,000 - ₹6,00,000\n• 🛠️ **Ongoing Maintenance**: ₹10,000 - ₹50,000/month\n\nWe provide itemized, honest cost scoping before any commitment.\n\nWould you like a detailed quote for your specific project?`,

    portfolio: `We specialize in crafting lightweight digital solutions and custom web applications. Some of our featured live client projects include:\n\n🛍️ **Vellankanni Stores (Dileo)** - Fresh produce & grocery e-commerce web app (https://www.dileo.co.in/)\n👗 **MJ Clothing** - Full-stack fashion e-commerce platform (https://www.mjclothing.co.in/)\n🚚 **DD Service** - Ashok Leyland & LCV vehicle repair booking portal (https://ddservice.netlify.app/)\n\nWe focus on direct co-founder communication, instant load speeds, and clean, optimized code. What kind of project do you have in mind?`,

    technology: `We leverage a modern, lightweight technology stack to build robust solutions:\n\n**Frontend:**\n• React, Next.js, and TypeScript\n• Tailwind CSS for responsive styling\n• Framer Motion for clean micro-animations\n\n**Backend & Cloud:**\n• Node.js, Python, PostgreSQL, and Firebase\n• Cloud hosting via AWS, Vercel, or Netlify\n\n**Mobile:**\n• React Native and Flutter for cross-platform deployments\n\nWhat type of stack are you planning to use?`,

    timeline: `Our project delivery timelines are fast and structured:\n\n⚡ **Marketing Website**: 1-3 weeks\n🏢 **Business Dashboard**: 4-8 weeks\n🛒 **E-commerce Platform**: 6-12 weeks\n📱 **Cross-Platform Mobile App**: 8-12 weeks\n🏗️ **Custom Business Software**: 6-12 weeks\n\n**Our Workflow:**\n1. Discovery & Discussion (1-2 weeks)\n2. UI/UX Figma Design (2-3 weeks)\n3. Code Development & Testing (varies)\n4. Cloud Launch & Support (1 week)\n\nWe send regular weekly updates. What is your target timeline?`,

    support: `We stand by our code long after launch. Every project includes **3 months of free maintenance**, which covers:\n\n✓ Server uptime monitoring\n✓ Security package updates\n✓ Database backup configurations\n✓ Small text/image updates\n\nWe also offer flexible monthly maintenance plans to serve as your external technical support team. Would you like to know more?`,

    consultation: `We would be happy to discuss your project requirements! Our consultation is free and carry zero pressure.\n\nTo schedule a call:\n📧 Email: bytepairs2026@gmail.com\n📞 Phone / WhatsApp: +91 6374241548\n📍 Location: Dharapuram, Tamil Nadu, India\n\nWhat is the best time for you to chat this week?`,

    default: `Thank you for your question! I'm here to help with information about:\n\n• 💰 Pricing and packages\n• 🎨 Our capabilities and portfolios\n• ⚙️ Technology stack and tools\n• ⏱️ Timelines and workflow\n• 🛠️ Support and maintenance plans\n• 📅 Scheduling a free consultation\n\nHow can I help you today?`
  };

  const generateAIResponse = (userMessage) => {
    const message = userMessage.toLowerCase();

    if (message.includes('pricing') || message.includes('cost') || message.includes('price') || message.includes('budget')) {
      return aiResponses.pricing;
    } else if (message.includes('portfolio') || message.includes('work') || message.includes('examples') || message.includes('case study')) {
      return aiResponses.portfolio;
    } else if (message.includes('technology') || message.includes('tech') || message.includes('stack') || message.includes('framework')) {
      return aiResponses.technology;
    } else if (message.includes('timeline') || message.includes('time') || message.includes('long') || message.includes('duration')) {
      return aiResponses.timeline;
    } else if (message.includes('support') || message.includes('maintenance') || message.includes('help') || message.includes('assistance')) {
      return aiResponses.support;
    } else if (message.includes('consultation') || message.includes('meeting') || message.includes('call') || message.includes('schedule')) {
      return aiResponses.consultation;
    } else if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return `Hello! Welcome to Byte Pairs! 👋\n\nI'm your AI assistant, ready to help you with:\n• Project planning and pricing\n• Technology recommendations\n• Portfolio details\n• Scheduling consultations\n\nWhat would you like to know about our services?`;
    } else {
      return aiResponses.default;
    }
  };

  const handleSendMessage = async (messageContent) => {
    if (!messageContent.trim()) return;

    // Add user message
    const userMessage = {
      content: messageContent,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShowWelcome(false);
    setIsTyping(true);

    try {
      // Call Groq API
      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_GROQ_API_KEY}`
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          messages: [
            {
              role: 'system',
              content: `You are a helpful AI assistant for Byte Pairs, a small, skilled startup software development company based in Dharapuram, Tamil Nadu, India.
              
Company Information:
- Email: bytepairs2026@gmail.com
- Phone / WhatsApp: +91 6374241548
- Location: Dharapuram, Tamil Nadu, India
- Services: Custom Software Development, Web Portals, Mobile Apps (React Native, Flutter), AI & Business Automation, Websites & Local SEO.
- Technologies: React, Next.js, TypeScript, Node.js, Python, PostgreSQL, AWS, Vercel.
- Pricing: Website (₹15,000 - ₹50,000), Web App (₹50,000 - ₹3,00,000), E-commerce (₹1,50,000 - ₹6,00,000).
- Timelines: Website (1-3 weeks), Custom Software/Web App (4-12 weeks).

Be friendly, transparent, professional, and helpful. Frame your answers around direct co-founder communication and zero software bloat. If asked about booking a call, direct them to email bytepairs2026@gmail.com or call +91 6374241548.`
            },
            ...messages.map(msg => ({
              role: msg.isUser ? 'user' : 'assistant',
              content: msg.content
            })),
            {
              role: 'user',
              content: messageContent
            }
          ],
          temperature: 0.7,
          max_tokens: 1024
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content || 'Sorry, I encountered an error. Please try again.';

      const assistantMessage = {
        content: aiResponse,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error calling Groq API:', error);
      const errorMessage = {
        content: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment or contact us directly at bytepaire@gmail.com or +91 6374241548.',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleStartConversation = (initialMessage) => {
    setShowWelcome(false);
    handleSendMessage(initialMessage);
  };

  const handleQuickAction = (message) => {
    handleSendMessage(message);
  };

  const openChat = () => {
    setIsChatOpen(true);
    setIsMinimized(false);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setIsMinimized(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  return (
    <>
      <Helmet>
        <title>24/7 AI Business Assistant | Byte Pairs</title>
        <meta
          name="description"
          content="Interact with our 24/7 AI Assistant to get instant scoping details on pricing, delivery timelines, project capabilities, and website SEO solutions."
        />
        <meta name="keywords" content="AI assistant, automated project pricing, CRM costing, software timelines, Byte Pairs support, AI chatbot" />
        <meta property="og:title" content="24/7 AI Business Assistant | Byte Pairs" />
        <meta property="og:description" content="Interact with our AI Assistant to get instant details on pricing, delivery timelines, and project capabilities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bytepairs.com/ai-assistant-interface-intelligent-support" />
        <link rel="canonical" href="https://bytepairs.com/ai-assistant-interface-intelligent-support" />

        {/* WebPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "24/7 AI Business Assistant | Byte Pairs",
            "description": "Interact with our 24/7 AI Assistant to get instant scoping details on pricing, delivery timelines, project capabilities, and website SEO solutions.",
            "url": "https://bytepairs.com/ai-assistant-interface-intelligent-support"
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://bytepairs.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "AI Assistant Support",
                "item": "https://bytepairs.com/ai-assistant-interface-intelligent-support"
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gradient-mesh">
        <Header />

        {/* Main Content */}
        <main className="pt-16">
          {/* Hero Section */}
          <section className="relative py-20 px-6 lg:px-8 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5"></div>

            <div className="relative max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6 shadow-brand-lg">
                  <Icon name="Bot" size={40} color="white" />
                </div>

                <h1 className="text-4xl lg:text-6xl font-bold text-foreground mb-6">
                  Meet Your
                  <span className="text-gradient block">AI Assistant</span>
                </h1>

                <p className="text-xl text-text-secondary leading-relaxed max-w-2xl mx-auto mb-8">
                  Get instant answers about our services, pricing, and capabilities.
                  Our intelligent assistant is available 24/7 to guide your digital transformation journey.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={openChat}
                    iconName="MessageCircle"
                    iconPosition="left"
                    className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand-lg"
                  >
                    Start Conversation
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Play"
                    iconPosition="left"
                    className="border-primary/20 text-primary hover:bg-primary/5"
                  >
                    Watch Demo
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-20 px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Intelligent Support Features
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Our AI assistant combines advanced technology with deep knowledge
                  of our services to provide personalized assistance.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    icon: 'Zap',
                    title: 'Instant Responses',
                    description: 'Get immediate answers to your questions about pricing, timelines, and technical capabilities without waiting for business hours.',
                    color: 'from-yellow-500 to-orange-500'
                  },
                  {
                    icon: 'Brain',
                    title: 'Context Awareness',
                    description: 'Our AI remembers your conversation history and preferences to provide increasingly personalized recommendations.',
                    color: 'from-purple-500 to-pink-500'
                  },
                  {
                    icon: 'Users',
                    title: 'Human Handoff',
                    description: 'Seamlessly connect with our expert team when you need detailed technical consultation or custom project planning.',
                    color: 'from-blue-500 to-cyan-500'
                  },
                  {
                    icon: 'BookOpen',
                    title: 'Knowledge Base',
                    description: 'Access comprehensive information about our services, technologies, case studies, and development processes.',
                    color: 'from-green-500 to-emerald-500'
                  },
                  {
                    icon: 'Calendar',
                    title: 'Smart Scheduling',
                    description: 'Book consultations, get project estimates, and schedule follow-ups directly through the chat interface.',
                    color: 'from-red-500 to-pink-500'
                  },
                  {
                    icon: 'Shield',
                    title: 'Secure & Private',
                    description: 'All conversations are encrypted and handled with complete confidentiality. Your data privacy is our priority.',
                    color: 'from-indigo-500 to-purple-500'
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="card p-6 hover:shadow-brand-lg transition-all duration-300"
                  >
                    <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                      <Icon name={feature.icon} size={24} color="white" />
                    </div>

                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      {feature.title}
                    </h3>

                    <p className="text-text-secondary leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-6 lg:px-8 bg-gradient-to-r from-primary/10 to-accent/10">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                  Ready to Start Your Project?
                </h2>

                <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                  Our AI assistant is standing by to help you explore possibilities,
                  get pricing information, and connect you with our expert team.
                </p>

                <Button
                  variant="default"
                  size="lg"
                  onClick={openChat}
                  iconName="MessageCircle"
                  iconPosition="left"
                  className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand-lg"
                >
                  Chat with AI Assistant
                </Button>
              </motion.div>
            </div>
          </section>
        </main>

        {/* Floating Chat Button */}
        <AnimatePresence>
          {!isChatOpen && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-6 right-6 z-40"
            >
              <Button
                variant="default"
                size="icon"
                onClick={openChat}
                className="w-14 h-14 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand-lg hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <Icon name="MessageCircle" size={24} color="white" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Interface */}
        <AnimatePresence>
          {isChatOpen && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
                height: isMinimized ? 'auto' : '600px'
              }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-3rem)] bg-background border border-border rounded-lg shadow-2xl z-50 overflow-hidden"
            >
              <ChatHeader
                onClose={closeChat}
                onMinimize={toggleMinimize}
                isMinimized={isMinimized}
              />

              {!isMinimized && (
                <>
                  {showWelcome ? (
                    <WelcomeMessage onStartConversation={handleStartConversation} />
                  ) : (
                    <div className="flex flex-col h-[536px]">
                      <ConversationArea
                        messages={messages}
                        isTyping={isTyping}
                      />

                      <div className="border-t border-border bg-background">
                        <ChatInput
                          onSendMessage={handleSendMessage}
                          disabled={isTyping}
                          placeholder="Ask me anything about bytepairs' services..."
                        />

                        <QuickActions
                          onActionClick={handleQuickAction}
                          disabled={isTyping}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <Footer />
      </div>
    </>
  );
};

export default AIAssistantInterface;